import styled, {createGlobalStyle} from "styled-components";
import {fontFamilies} from "theme/tokens";
import {INTERACTIVE_SELECTOR} from "./scrollReach";

export const Bar = styled.div`
	position: fixed;
	left: 16px;
	right: 16px;
	bottom: 16px;
	z-index: 1200;
	display: flex;
	align-items: center;
	gap: 18px;
	max-width: 960px;
	margin: 0 auto;
	padding: 16px 20px;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: 16px;
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	font-family: ${fontFamilies.sans};

	/* A WIDER TEXT COLUMN ON LARGE WINDOWS, because the bar's height is the landing's budget
	   (owner-approved, ui#2596). The notice grew by a sentence there, which took Romanian from two
	   lines to three and put the landing's campaign copy 2px over the bar at 1366x768
	   (measured on the landing). 1100px of bar gives the text roughly 110
	   characters a line instead of 90, which returns the Romanian sentence to two lines and the copy
	   its room. Only above 1200px, where the window has the width to spare. 901x700 is not covered
	   by this rule; there the landing clears the bar from its own side (owner decision 2026-09-18,
	   ui#2596): CampaignSurface.styles gives the campaign reading a bottom inset of
	   --consent-bar-reserve minus the hero's own inset. */
	@media (min-width: 1201px) {
		max-width: 1100px;
	}

	/* DOCKED TO THE VIEWPORT'S EDGES wherever the window cannot give the text column more room
	   (ui#2704): a sheet rather than a floating card, below the 1201px line above which the rule
	   above widens the card instead. The first layer now names the two advertising providers and the
	   audience purpose, which the DSK Orientierungshilfe (Rn. 51 and 53) requires of a layer whose
	   button grants that purpose, and that sentence is a line or two everywhere the column is
	   narrow. The bar's height is a budget on three surfaces: the 320x568 landing gate and the
	   375x667 recipient card (see below and Text), and the side-by-side landing from 901px, where
	   the campaign copy rests above the bar (CampaignSurface.styles) and the German newspaper slide
	   ran 7px into a 139px bar at 901x700. Docking gives the bar the room the 16px insets were
	   spending: 32px of width for the text column, and the 16px bottom inset, which consentClearance
	   measures rather than assumes, so the strip reserved above the bar shrinks by the same 16px
	   the bar moved down. */
	@media (max-width: 1200px) {
		left: 0;
		right: 0;
		bottom: 0;
		border-bottom: none;
		border-radius: 16px 16px 0 0;
	}

	@media (max-width: 720px) {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
		/* The box follows the type down (see Text below): 12px against 12px copy. It is a BUDGET
		   rather than a taste — the recipient download page is viewport-locked and lays its card out
		   ABOVE this bar, so every pixel of bar height is a pixel that card does not get. At 375x667
		   the card's fixed parts plus the file list's hard floor need 398px; the window gives 667 less
		   the 80px header clearance less the reserve, and the reserve is this bar plus its measured
		   inset plus the 16px gap — so with the bar docked (inset 0) a bar over 173px puts the card
		   into a scroll of its own. Measured at 375x667 (ui#2704): the notice is five lines at 12px/1.4
		   in en, ro and de, the bar lands at 165px and the reserve at 181px, which is the figure the
		   file list's floor in download/recipient/primitives.tsx is measured against. Before the
		   docking, the same five lines in a floating bar were 172px against a 157px budget: naming
		   the accept button (ui#2596) had taken 16px of it, and the two providers plus the audience
		   purpose (ui#2704) a whole line more. The 12px side padding is part of the same budget: at
		   16px the German notice wraps to a sixth line at 375px. */
		padding-block: 12px;
		padding-inline: 12px;
	}
`;

export const Text = styled.p`
	margin: 0;
	flex: 1 1 auto;
	font-size: 13.5px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};

	/* THE PHONE TYPE SCALE IS A LAYOUT BUDGET, not a style preference (owner-approved, ui#2596).
	   Naming the button that grants the email purpose made the notice a sentence longer, which at
	   320px is two more lines: the bar reached 238px of a 568px screen in German and covered the
	   create card's „Ich stimme zu“ control by 39px (22px in Romanian). Reserving the strip the way
	   the download page does cannot answer that — the gate's own slack between notice and control is
	   0px in German, and the card is centred in its container, so padding there moves the control
	   DOWN. Six German lines at 13.5px/1.55 come to 126px; five German lines at 12px/1.4 come to
	   84px, which with the bar's smaller block padding above cleared the control by 6px rather than
	   covering it by 39px (measured; English had 27px and Romanian 24px). 12px is the size the
	   actions in this same bar already use below 720px, so the bar keeps one type scale per
	   viewport. Naming the providers and the audience purpose (ui#2704) then cost a sixth line in
	   every locale at 320px, which the docked bar above pays for: measured at 320x568 the bar is
	   182px in en and ro and 187px in de, and the control clears it by 33px and 28px.
	   home-hero-editorial.spec.ts checks the English control is operable under the bar at 320x568.

	   THE SAME SCALE APPLIES UP TO 1200px since ui#2704, for the same reason: between 721px and
	   1200px the text shares its row with the three buttons and the card cannot widen, so the German
	   notice at 13.5px was five lines beside 344px of German button labels at 901x700, and that
	   139px bar reached the campaign copy of the tallest slide by 7px. At 12px/1.4 the docked bar is
	   101px there in de and ro and 84px in en, and the same slide clears it by 20px in every locale
	   (measured at 901x700). Above 1200px the card widens instead and keeps the 13.5px scale. */
	@media (max-width: 1200px) {
		font-size: 12px;
		line-height: 1.4;
	}
`;

export const PolicyLink = styled.button`
	display: inline;
	padding: 0;
	border: none;
	background: none;
	font: inherit;
	color: ${({theme}) => theme.tokens.blue};
	text-decoration: underline;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;

	@media (max-width: 720px) {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
`;

/** Shared button base — geometry the three actions share. */
const ActionButton = styled.button`
	height: 40px;
	padding: 0 16px;
	border-radius: 11px;
	font-family: ${fontFamilies.sans};
	font-weight: 600;
	font-size: 13.5px;
	cursor: pointer;
	transition: 0.16s;
	white-space: nowrap;

	@media (max-width: 720px) {
		min-width: 0;
		min-height: 44px;
		height: auto;
		padding: 8px 4px;
		font-size: 12px;
		line-height: 1.3;
		white-space: normal;
		overflow-wrap: anywhere;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * While the banner is up, every scroll-into-view (keyboard focus, click actionability, anchor
 * jumps) must land interactive controls ABOVE the floating Bar, not at the viewport's bottom edge
 * underneath it — browsers scroll only until "visible", which is exactly the covered strip.
 * `scroll-margin-bottom` is honored by those scrolls, costs nothing in layout, and unmounts with
 * the banner the moment consent is decided.
 */
export const ScrollClearance = createGlobalStyle<{clearance: number}>`
	${INTERACTIVE_SELECTOR} {
		scroll-margin-bottom: ${({clearance}) => clearance}px;
	}
`;

/**
 * ALL THREE ACTIONS ARE OUTLINED since owner decision 2026-09-23 (ui#2853): no fill, a 1px edge, and
 * ink from the tokens. Both components below share that treatment and differ only in which ink they
 * take — the accent for the two answers, neutral for Customize.
 *
 * None of the three takes a hover FILL, and that is a contrast rule rather than a taste. The bar's
 * surface is `card`, which is the lightest surface in light mode and the darkest in dark mode, so any
 * tint painted behind the label moves the background TOWARD the ink and lowers the label's contrast
 * in both modes at once — measured on `accentTint`: the answers' hover would read 6.98:1 instead of
 * 8.08:1 in light, and 5.33:1 in dark, which is BELOW their own 5.46:1 resting figure. Hovering would
 * make the control harder to read.
 *
 * So the hover is carried by the EDGE instead: the border moves to the deeper ink (which raises
 * contrast, numbers per component below) and a second ring of the same colour is painted flush
 * outside it. The ring is a `box-shadow` rather than a wider border because a border that grows takes
 * its width out of the content box, and on a phone the three labels sit inside 101px of content at
 * 375px and 82px at 320px (measured) — a German label re-wrapping on hover would move the bar's own
 * height, which the recipient card's fit budget is measured against. A shadow changes no box at all.
 * Without it the whole hover was `blue` → `blued` on one hairline and on the label: a state change a
 * mouse user cannot see, where the filled design it replaces repainted a 97x40 block.
 *
 * `:focus-visible` is the shared 2px ring on `ActionButton`, drawn at a 2px offset, and is untouched
 * — it stays distinguishable from the hover ring, which is flush against the border.
 */

/**
 * The Customize action (design `.abtn` secondary) — outlined in neutral ink.
 *
 * It is not one of the two answers (it opens the settings dialog), so it carries no accent and stays
 * the quieter control of the row. It was a borderless `field` fill from owner 2026-08-16 (ui#1100)
 * until this change; a fill is what the 2026-09-23 decision removes, so "borderless" is no longer the
 * rule for it and the edge below is deliberate.
 *
 * The edge is `ink2` rather than `line`/`cardBorder`/`fieldLine`: those measure 1.26:1, 1.24:1 and
 * 1.31:1 against the bar's `card` surface in light mode, which is not a boundary a sighted user can
 * see and is far under the 3:1 WCAG 1.4.11 asks of a control's edge (`ink3` reaches only 2.62:1
 * there). `ink2` measures 6.50:1 light / 7.60:1 dark — the same weight the answers' blue edge
 * carries, so "quieter" is a matter of hue, never of visibility. The label keeps `ink`: 17.92:1 light
 * / 15.71:1 dark, up from 15.98:1 / 15.08:1 on the old `field` fill.
 */
export const CustomizeButton = styled(ActionButton)`
	background: transparent;
	border: 1px solid ${({theme}) => theme.tokens.ink2};
	color: ${({theme}) => theme.tokens.ink};

	&:hover {
		border-color: ${({theme}) => theme.tokens.ink};
		box-shadow: 0 0 0 1px currentColor;
	}
`;

/**
 * The two ANSWERS — Accept all AND Reject, rendered from this one component so they are identical in
 * fill, edge, ink, size, row and order, and there is no styling difference left for a reader to
 * weigh.
 *
 * That identity is the rule, and it is the whole rule. ui#2359 gave Reject an outline BESIDE a filled
 * Accept and ui#2596 withdrew it; what was withdrawn was the ASYMMETRY, not the outline. Outlining
 * both answers together (owner 2026-09-23, ui#2853) keeps ui#2202's record intact — EDPB
 * cookie-banner taskforce report (January 2023): accept and refuse presented with equal prominence;
 * VG Hannover 10 A 5385/22: a visually preferred accept makes the consent invalid; Italian Garante
 * 2021: formatted identically; Dutch AP 2026: no less prominent. Do not give either answer a
 * component, a fill or an ink the other does not have; `e2e/cookie-banner-prominence.spec.ts` reads
 * the painted pixels back and fails in both directions.
 *
 * Contrast against the bar's `card` surface: the blue label and edge measure 6.31:1 light / 5.46:1
 * dark at rest (the filled version measured 6.31:1 / 5.95:1 — in light mode the identical number,
 * because a contrast ratio is symmetric and only the two colours swapped places), and hover deepens
 * both edge and label to `blued` for 8.08:1 light / 6.65:1 dark. Every state clears WCAG AA for text
 * and 1.4.11 for the edge; the dark mode's 0.49 of a point is the whole cost of the change, and it is
 * paid because `card` is lighter than `blueInk`, not by any choice of ink.
 */
export const AnswerButton = styled(ActionButton)`
	background: transparent;
	border: 1px solid ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blue};

	&:hover {
		border-color: ${({theme}) => theme.tokens.blued};
		color: ${({theme}) => theme.tokens.blued};
		box-shadow: 0 0 0 1px currentColor;
	}
`;

